import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Text,
  VStack,
  HStack,
  Switch,
  FormControl,
  FormLabel,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface InputManifest {
  input_targets: string[];
  default_run_targets: string[];
  allowed_run_targets: string[];
  submit_targets: string[];
  allowed_cilksan_targets: string[];
  default_cilksan_target: string;
  default_cilkscale_target: string;
}

interface ContestInputSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  problemId: string;
  onSettingsChange: (settings: {
    selectedInputs: string[];
    enableParallelAnalysis: boolean;
  }) => void;
}

export default function ContestInputSelectionModal({
  isOpen,
  onClose,
  problemId,
  onSettingsChange,
}: ContestInputSelectionModalProps) {
  const { getAccessToken } = useAuth();
  const toast = useToast();
  const [inputManifest, setInputManifest] = useState<InputManifest | null>(null);
  const [selectedInputs, setSelectedInputs] = useState<string[]>([]);
  const [enableParallelAnalysis, setEnableParallelAnalysis] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load input manifest when modal opens
  useEffect(() => {
    if (isOpen && problemId) {
      loadInputManifest();
    }
  }, [isOpen, problemId]);

  const loadInputManifest = async () => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await api.get(`/problems/${problemId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const manifest = response.data.input_manifest;
      if (manifest) {
        setInputManifest(manifest);
        // Default to all allowed run targets
        setSelectedInputs(manifest.allowed_run_targets || manifest.input_targets);
      }
    } catch (error) {
      console.error('Failed to load input manifest:', error);
      toast({
        title: 'Error',
        description: 'Failed to load input manifest',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputToggle = (inputName: string) => {
    setSelectedInputs(prev => {
      if (prev.includes(inputName)) {
        return prev.filter(name => name !== inputName);
      } else {
        return [...prev, inputName];
      }
    });
  };

  const handleSelectAll = () => {
    if (inputManifest) {
      setSelectedInputs(inputManifest.allowed_run_targets || inputManifest.input_targets);
    }
  };

  const handleSelectNone = () => {
    setSelectedInputs([]);
  };

  const handleSave = () => {
    if (selectedInputs.length === 0) {
      toast({
        title: 'No Inputs Selected',
        description: 'Please select at least one input dataset',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onSettingsChange({
      selectedInputs,
      enableParallelAnalysis,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text>⚙️</Text>
            <Text>Run Settings</Text>
          </HStack>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Test & Benchmark Inputs Section */}
            <VStack align="stretch" spacing={3}>
              <Text fontSize="lg" fontWeight="semibold">
                Test & Benchmark Inputs
              </Text>
              <Text fontSize="sm" color="gray.600">
                Select test inputs from the options below.
              </Text>
              
              {inputManifest && (
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="medium">
                      Available Inputs ({inputManifest.input_targets.length})
                    </Text>
                    <HStack spacing={2}>
                      <Button size="xs" onClick={handleSelectAll}>
                        Select All
                      </Button>
                      <Button size="xs" onClick={handleSelectNone}>
                        Select None
                      </Button>
                    </HStack>
                  </HStack>
                  
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Input Name</Th>
                        <Th>Select</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {inputManifest.input_targets.map((inputName) => (
                        <Tr key={inputName}>
                          <Td>
                            <Text fontSize="sm">{inputName}</Text>
                          </Td>
                          <Td>
                            <Checkbox
                              isChecked={selectedInputs.includes(inputName)}
                              onChange={() => handleInputToggle(inputName)}
                              colorScheme="blue"
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </VStack>
              )}
            </VStack>

            {/* Parallel Analysis Section */}
            <VStack align="stretch" spacing={3}>
              <Text fontSize="lg" fontWeight="semibold">
                Parallel Analysis
              </Text>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="parallel-analysis" mb="0" fontSize="sm">
                  Enable Cilksan/Cilkscale parallel analysis
                </FormLabel>
                <Switch
                  id="parallel-analysis"
                  isChecked={enableParallelAnalysis}
                  onChange={(e) => setEnableParallelAnalysis(e.target.checked)}
                  colorScheme="blue"
                />
              </FormControl>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save Settings
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
} 